/**
 * @file PresentationSequenceConfig.ts
 * @role Validated contract for event-driven presentation choreography: which decorative
 *       channels run which named command, in what order, for how long, under which quality and
 *       reduced-motion policy. It is data only — the config names presets that other configs
 *       already define, and can express no callback, expression, asset URL, or outcome.
 * @owns The choreography schema, its bounds/uniqueness rules, and the cross-config preset
 *       references GameSkin resolves at boot.
 * @reads A raw config object supplied by the caller. It deliberately does NOT import the bundled
 *        JSON: GameSkin is the one composition root for this config (Invariant 13).
 * @neverDoes Decide outcomes, wins, balances, or awarded spin counts. Import a channel
 *            implementation. Read the clock. Hold mutable runtime state.
 * @invariants 1, 2, 5, 10 (config is validated before use), 13, 20.
 * @see docs/decisions/0010-explicit-presentation-choreography.md, docs/CONFIG_GUIDE.md,
 *      src/game/presentation/PresentationSequencePlanner.ts
 */
import { z } from 'zod';
import { ConcreteGraphicsQualitySchema } from '@core/graphics/GraphicsQualityService';
import { GameError } from '@core/errors/GameError';
import { SOUND_IDS } from '@core/audio/SoundCatalog';
import {
  PRESENTATION_CHANNELS,
  PRESENTATION_COMMANDS,
  REDUCED_MOTION_SAFE_COMMANDS,
  type PresentationChannel,
  type PresentationStep,
} from './PresentationChannels';

/* -------------------------------------------------------------------------- */
/* Triggers, cues, context selectors, and priority                             */
/* -------------------------------------------------------------------------- */

/** The named game moments a sequence may attach to. The owning presenter calls these directly;
 *  none of them is discovered by subscribing to the EventBus. */
export const PRESENTATION_TRIGGERS = [
  'spin.start',
  'spin.noWin',
  'win',
  'feature.intro',
  'feature.spinStarted',
  'feature.countdown',
  'feature.outro',
  'error',
] as const;

export type PresentationTrigger = (typeof PRESENTATION_TRIGGERS)[number];

/**
 * Named moments WITHIN a running sequence, reported by the presenter that owns the explanation.
 *
 * These exist because the explanatory timeline is outcome-dependent: a one-line win and a
 * four-line win differ by seconds, so a payoff beat pinned to an absolute offset would drift.
 * A phase anchored to a cue waits for it instead of guessing when it happens.
 */
export const PRESENTATION_CUES = [
  'anticipationStarted',
  'symbolsStarted',
  'symbolsCompleted',
  'paylineCompleted',
  'amountCountStarted',
  'amountCountCompleted',
  'presentationExiting',
  'featureIntroFaceShown',
  'featureIntroCompleted',
] as const;

export type PresentationCue = (typeof PRESENTATION_CUES)[number];

/**
 * The complete set of selectors a sequence may branch on. Deliberately closed: a sequence can
 * react to a classification the owning flow already made, and can express nothing else. There is
 * no selector for an amount, a symbol, a balance, or a payline — those are outcomes, not context.
 *
 * Presentation SPEED is deliberately absent. It is a run option, not a selector: Quick Spin runs
 * the same score faster, it does not choose a different one, and the authoritative compression
 * factor already lives in wins.config.json.
 */
const ContextSchema = z
  .object({
    winTier: z.enum(['small', 'medium', 'big', 'jackpot']).optional(),
    featureContext: z.enum(['base', 'freeSpins']).optional(),
    remainingBucket: z.enum(['3', '2', '1']).optional(),
    outroTotal: z.enum(['zero', 'positive']).optional(),
  })
  .strict();

export type PresentationContext = z.infer<typeof ContextSchema>;

const CONTEXT_FIELDS = [
  'winTier',
  'featureContext',
  'remainingBucket',
  'outroTotal',
] as const satisfies readonly (keyof PresentationContext)[];

/**
 * Arbitration order from issue #105: which LOOK wins when two decorative moments overlap.
 *
 * It deliberately does not answer "may I take over" on its own — a cleanup sequence is visually
 * unimportant but must always run. That second axis is the separate `interrupts` flag.
 */
export const PRESENTATION_PRIORITIES = [
  'idle',
  'spinCleanup',
  'small',
  'medium',
  'big',
  'jackpot',
  'featureTransition',
  'sceneExit',
  'error',
] as const;

export type PresentationPriority = (typeof PRESENTATION_PRIORITIES)[number];

const PRIORITY_RANK = Object.freeze(
  Object.fromEntries(PRESENTATION_PRIORITIES.map((name, index) => [name, index])),
) as Readonly<Record<PresentationPriority, number>>;

/** Numeric rank for a named priority. Higher wins. */
export function priorityRank(priority: PresentationPriority): number {
  return PRIORITY_RANK[priority];
}

/* -------------------------------------------------------------------------- */
/* Steps                                                                       */
/* -------------------------------------------------------------------------- */

const MAX_TIME_MS = 60_000;

/**
 * Timing and policy shared by every step, spread into each channel schema so the union stays
 * discriminable — zod's `discriminatedUnion` rejects members carrying effects or intersections,
 * and (as of 3.25) cannot nest one union inside another.
 */
const commonStepFields = {
  atMs: z.number().nonnegative().max(MAX_TIME_MS).default(0),
  durationMs: z.number().nonnegative().max(MAX_TIME_MS).default(0),
  minQuality: ConcreteGraphicsQualitySchema.optional(),
  reducedMotion: z.enum(['keep', 'omit', 'substitute']).default('keep'),
} as const;

/**
 * Builds one channel's step schema. The command enum and its `substituteCommand` twin are read
 * from the same table entry, so they cannot drift apart by copy-paste — pairing a channel with
 * another channel's command list stops being expressible.
 *
 * The static `command` type widens to every channel's commands because `channel` is generic;
 * only the runtime enum is per-channel, and that is what validation uses. The precise
 * per-command shapes are recovered at the validation boundary in `validatePresentationSequencesConfig`.
 */
function channelStep<C extends PresentationChannel, S extends z.ZodRawShape>(channel: C, extra: S) {
  const commands = PRESENTATION_COMMANDS[channel];
  return z.object({
    channel: z.literal(channel),
    command: z.enum(commands),
    substituteCommand: z.enum(commands).optional(),
    ...commonStepFields,
    ...extra,
  });
}

const StepSchema = z.discriminatedUnion('channel', [
  channelStep('title', {
    scope: z.enum(['all', 'title', 'subtitle', 'letter:0', 'word:0', 'word:1']).default('all'),
    animationPreset: z.string().min(1).optional(),
  }),
  channelStep('neon', {
    selector: z
      .string()
      .regex(/^(all|id:.+|group:.+)$/, 'unsupported neon selector')
      .default('all'),
    sequenceId: z.string().min(1).optional(),
    movementProfile: z.string().min(1).optional(),
    intensity: z.number().min(0).max(2).optional(),
  }),
  channelStep('particles', {
    preset: z.string().min(1).optional(),
    origin: z.enum(['winCenter', 'boardCenter', 'reelFrame']).default('boardCenter'),
  }),
  channelStep('postFx', {}),
  channelStep('ambient', {
    tier: z.enum(['idle', 'small', 'medium', 'big', 'jackpot']).optional(),
  }),
  channelStep('audio', { soundId: z.enum(SOUND_IDS) }),
  channelStep('featureStatus', { intensity: z.number().min(0).max(1).optional() }),
]);

/* -------------------------------------------------------------------------- */
/* Phases, sequences, and base contexts                                        */
/* -------------------------------------------------------------------------- */

const PhaseSchema = z.object({
  id: z.string().min(1),
  /** Offset from the sequence start, or from the cue when `anchor` is set. */
  atMs: z.number().nonnegative().max(MAX_TIME_MS).default(0),
  /** Wait for this cue instead of a fixed offset. A cue that never arrives simply never runs its
   *  phase — an optional beat is a safe no-op, never a stall. */
  anchor: z.enum(PRESENTATION_CUES).optional(),
  mode: z.enum(['serial', 'parallel']).default('parallel'),
  steps: z.array(StepSchema).min(1),
});

const SequenceSchema = z.object({
  id: z.string().min(1),
  trigger: z.enum(PRESENTATION_TRIGGERS),
  context: ContextSchema.default({}),
  priority: z.enum(PRESENTATION_PRIORITIES),
  /** Take over from ANY active sequence regardless of rank. Reserved for cleanup and error
   *  sequences, whose whole job is to run — a spin's cancel phase that a still-playing win could
   *  refuse would leak the previous win into the new spin. */
  interrupts: z.boolean().default(false),
  /** Hard ceiling. The coordinator completes at this point even if a step is still pending, and
   *  boot validation proves the authored timeline already finishes inside it. */
  maxDurationMs: z.number().positive().max(MAX_TIME_MS),
  phases: z.array(PhaseSchema).min(1),
  /** Run verbatim on completion, skip, cancel, reset, and dispose, BEFORE the unconditional
   *  clearTransient sweep. Only for beats the sweep cannot express, such as stopping a specific
   *  audio loop; restating what the sweep already does belongs nowhere. */
  cleanup: z.array(StepSchema).default([]),
});

/**
 * The persistent look a mode owns, expressed as ordinary steps so any channel can participate.
 * Re-applied after every transient cleanup, which is what keeps a big win inside free spins from
 * dropping the game back to the base-game background.
 *
 * `default` is required and is what "leaving the feature" applies, so clearing base context is
 * data rather than a special adapter verb.
 */
const BaseContextsSchema = z
  .record(z.string().min(1), z.array(StepSchema))
  .refine((contexts) => contexts.default !== undefined, {
    message: 'baseContexts must define "default" — it is what leaving a feature restores',
  });

const BaseConfigSchema = z.object({
  version: z.literal(1),
  baseContexts: BaseContextsSchema,
  sequences: z.array(SequenceSchema).min(1),
});

type RawConfig = z.infer<typeof BaseConfigSchema>;

/* -------------------------------------------------------------------------- */
/* Public types (precise, post-validation)                                     */
/* -------------------------------------------------------------------------- */

export interface PresentationPhase {
  readonly id: string;
  readonly atMs: number;
  readonly anchor?: PresentationCue;
  readonly mode: 'serial' | 'parallel';
  readonly steps: readonly PresentationStep[];
}

export interface PresentationSequence {
  readonly id: string;
  readonly trigger: PresentationTrigger;
  readonly context: PresentationContext;
  readonly priority: PresentationPriority;
  readonly interrupts: boolean;
  readonly maxDurationMs: number;
  readonly phases: readonly PresentationPhase[];
  readonly cleanup: readonly PresentationStep[];
}

export interface PresentationSequencesConfig {
  readonly version: 1;
  readonly baseContexts: Readonly<Record<string, readonly PresentationStep[]>>;
  readonly sequences: readonly PresentationSequence[];
}

/* -------------------------------------------------------------------------- */
/* Structural validation                                                       */
/* -------------------------------------------------------------------------- */

type RawStep = z.infer<typeof StepSchema>;

/** Commands that cannot act without an accompanying reference. This is the runtime half of the
 *  precise step types: once it passes, `PresentationStep`'s per-command shapes hold. */
function missingReferenceFor(step: RawStep): string | undefined {
  if (step.channel === 'neon' && step.command === 'runSequence' && !step.sequenceId) {
    return 'neon runSequence requires a sequenceId';
  }
  if (step.channel === 'neon' && step.command === 'setMovementProfile' && !step.movementProfile) {
    return 'neon setMovementProfile requires a movementProfile';
  }
  if (step.channel === 'particles' && step.command === 'emit' && !step.preset) {
    return 'particles emit requires a preset';
  }
  if (step.channel === 'ambient' && step.command === 'setTier' && !step.tier) {
    return 'ambient setTier requires a tier';
  }
  return undefined;
}

/** Fields a command must NOT carry, so a dead reference cannot be boot-validated as a real one. */
function strayReferenceFor(step: RawStep): string | undefined {
  if (step.channel === 'neon' && step.command !== 'runSequence' && step.sequenceId) {
    return `neon ${step.command} does not use sequenceId`;
  }
  if (step.channel === 'neon' && step.command !== 'setMovementProfile' && step.movementProfile) {
    return `neon ${step.command} does not use movementProfile`;
  }
  if (step.channel === 'particles' && step.command !== 'emit' && step.preset) {
    return `particles ${step.command} does not use preset`;
  }
  if (step.channel === 'ambient' && step.command !== 'setTier' && step.tier) {
    return `ambient ${step.command} does not use tier`;
  }
  return undefined;
}

function validateStep(step: RawStep, ctx: z.RefinementCtx, path: (string | number)[]): void {
  for (const problem of [missingReferenceFor(step), strayReferenceFor(step)]) {
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem, path });
  }

  if (step.reducedMotion !== 'substitute') {
    if (step.substituteCommand) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'substituteCommand is only meaningful with reducedMotion substitute',
        path: [...path, 'substituteCommand'],
      });
    }
    return;
  }
  if (!step.substituteCommand) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'reducedMotion substitute requires a substituteCommand',
      path: [...path, 'substituteCommand'],
    });
    return;
  }
  // Every safe command is field-free, so a substitution can never strand a required reference.
  // Checking membership is therefore the whole check.
  const safe: readonly string[] = REDUCED_MOTION_SAFE_COMMANDS[step.channel];
  if (!safe.includes(step.substituteCommand)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${step.substituteCommand} is not reduced-motion safe for channel ${step.channel} (allowed: ${safe.join(', ')})`,
      path: [...path, 'substituteCommand'],
    });
  }
}

function contextKeyOf(context: PresentationContext): string {
  return CONTEXT_FIELDS.filter((field) => context[field] !== undefined)
    .map((field) => `${field}=${context[field]}`)
    .join('&');
}

/** Longest wall-clock offset the authored phases can reach, ignoring which quality or motion
 *  preference is active — dropping or substituting a step only ever shortens a plan. Anchored
 *  phases are measured from their own cue, since when that cue arrives is not knowable here. */
function authoredBounds(sequence: z.infer<typeof SequenceSchema>): {
  timelineMs: number;
  longestAnchorTailMs: number;
} {
  let timelineMs = 0;
  let longestAnchorTailMs = 0;
  for (const phase of sequence.phases) {
    let cursorMs = phase.atMs;
    let phaseEndMs = 0;
    for (const step of phase.steps) {
      const startMs = phase.mode === 'serial' ? cursorMs + step.atMs : phase.atMs + step.atMs;
      if (phase.mode === 'serial') cursorMs = startMs + step.durationMs;
      phaseEndMs = Math.max(phaseEndMs, startMs + step.durationMs);
    }
    if (phase.anchor) longestAnchorTailMs = Math.max(longestAnchorTailMs, phaseEndMs);
    else timelineMs = Math.max(timelineMs, phaseEndMs);
  }
  return { timelineMs, longestAnchorTailMs };
}

function validateStructure(config: RawConfig, ctx: z.RefinementCtx): void {
  for (const [id, steps] of Object.entries(config.baseContexts)) {
    steps.forEach((step, index) => validateStep(step, ctx, ['baseContexts', id, index]));
  }

  const seenIds = new Set<string>();
  const selectorsByTrigger = new Map<PresentationTrigger, { shape: string; id: string }>();
  const seenSelection = new Map<string, string>();

  config.sequences.forEach((sequence, index) => {
    const path: (string | number)[] = ['sequences', index];

    if (seenIds.has(sequence.id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `duplicate sequence id ${sequence.id}`,
        path: [...path, 'id'],
      });
    }
    seenIds.add(sequence.id);

    // Selection must be an exact key lookup, never a scoring contest. Requiring every sequence
    // on a trigger to branch on the SAME selectors makes "which sequence runs" answerable by
    // reading the config, and makes an ambiguous pair impossible rather than merely unlikely.
    const shape = CONTEXT_FIELDS.filter((field) => sequence.context[field] !== undefined).join(',');
    const known = selectorsByTrigger.get(sequence.trigger);
    if (known && known.shape !== shape) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `trigger ${sequence.trigger} mixes context selectors: ${sequence.id} uses [${shape || 'none'}] but ${known.id} uses [${known.shape || 'none'}]`,
        path: [...path, 'context'],
      });
    } else if (!known) {
      selectorsByTrigger.set(sequence.trigger, { shape, id: sequence.id });
    }

    const selection = `${sequence.trigger}|${contextKeyOf(sequence.context)}`;
    const duplicate = seenSelection.get(selection);
    if (duplicate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${sequence.id} selects the same trigger and context as ${duplicate}`,
        path: [...path, 'context'],
      });
    }
    seenSelection.set(selection, sequence.id);

    const seenPhaseIds = new Set<string>();
    const seenAnchors = new Set<string>();
    sequence.phases.forEach((phase, phaseIndex) => {
      if (seenPhaseIds.has(phase.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `duplicate phase id ${phase.id} in sequence ${sequence.id}`,
          path: [...path, 'phases', phaseIndex, 'id'],
        });
      }
      seenPhaseIds.add(phase.id);
      // One phase per cue keeps "what happens when the count ends" a single readable answer.
      if (phase.anchor) {
        if (seenAnchors.has(phase.anchor)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `sequence ${sequence.id} anchors more than one phase to ${phase.anchor}`,
            path: [...path, 'phases', phaseIndex, 'anchor'],
          });
        }
        seenAnchors.add(phase.anchor);
      }
      phase.steps.forEach((step, stepIndex) =>
        validateStep(step, ctx, [...path, 'phases', phaseIndex, 'steps', stepIndex]),
      );
    });

    sequence.cleanup.forEach((step, stepIndex) =>
      validateStep(step, ctx, [...path, 'cleanup', stepIndex]),
    );

    // Proving the ceiling here means the runtime hard stop is a backstop, never the thing that
    // ends an authored sequence. An anchored phase is bounded relative to its cue, so its tail
    // must also fit — otherwise a late cue could be silently truncated.
    const { timelineMs, longestAnchorTailMs } = authoredBounds(sequence);
    if (timelineMs > sequence.maxDurationMs) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `sequence ${sequence.id} runs ${timelineMs}ms but declares maxDurationMs ${sequence.maxDurationMs}`,
        path: [...path, 'maxDurationMs'],
      });
    }
    if (longestAnchorTailMs > sequence.maxDurationMs) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `sequence ${sequence.id} has an anchored phase running ${longestAnchorTailMs}ms past its cue, beyond maxDurationMs ${sequence.maxDurationMs}`,
        path: [...path, 'maxDurationMs'],
      });
    }
  });
}

const ValidatedConfigSchema = BaseConfigSchema.superRefine(validateStructure);

/**
 * Parse and structurally validate a raw choreography config.
 *
 * The zod pass is deliberately flat — it cannot express "sequenceId is required only when the
 * command is runSequence", because zod 3 cannot nest a discriminated union inside another. The
 * superRefine above closes that gap in both directions (missing AND stray references), which is
 * exactly what makes the precise per-command types in PresentationChannels sound. Past this
 * boundary the data is trusted, per Invariant 10.
 */
export function validatePresentationSequencesConfig(raw: unknown): PresentationSequencesConfig {
  const parsed = ValidatedConfigSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError(
      'CONFIG_LOAD_FAILED',
      `Invalid presentation-sequences config: ${parsed.error.message}`,
      { cause: parsed.error },
    );
  }
  return parsed.data as unknown as PresentationSequencesConfig;
}

/** The selectors a trigger's sequences branch on, in canonical order. */
export function contextSelectorsFor(
  config: PresentationSequencesConfig,
  trigger: PresentationTrigger,
): readonly (keyof PresentationContext)[] {
  const sequence = config.sequences.find((candidate) => candidate.trigger === trigger);
  if (!sequence) return [];
  return CONTEXT_FIELDS.filter((field) => sequence.context[field] !== undefined);
}

/* -------------------------------------------------------------------------- */
/* Cross-config references                                                     */
/* -------------------------------------------------------------------------- */

/** Every preset name this config borrows from another config, for GameSkin to cross-check. */
export interface PresentationSequenceReferences {
  readonly neonSequenceIds: ReadonlySet<string>;
  readonly neonMovementProfiles: ReadonlySet<string>;
  readonly particlePresets: ReadonlySet<string>;
  readonly titleAnimationPresets: ReadonlySet<string>;
}

export function collectPresentationSequenceReferences(
  config: PresentationSequencesConfig,
): PresentationSequenceReferences {
  const neonSequenceIds = new Set<string>();
  const neonMovementProfiles = new Set<string>();
  const particlePresets = new Set<string>();
  const titleAnimationPresets = new Set<string>();

  const visit = (step: PresentationStep): void => {
    if (step.channel === 'neon') {
      if (step.command === 'runSequence') neonSequenceIds.add(step.sequenceId);
      if (step.command === 'setMovementProfile') neonMovementProfiles.add(step.movementProfile);
    }
    if (step.channel === 'particles' && step.command === 'emit') particlePresets.add(step.preset);
    if (step.channel === 'title' && step.animationPreset) {
      titleAnimationPresets.add(step.animationPreset);
    }
  };

  for (const steps of Object.values(config.baseContexts)) for (const step of steps) visit(step);
  for (const sequence of config.sequences) {
    for (const phase of sequence.phases) for (const step of phase.steps) visit(step);
    for (const step of sequence.cleanup) visit(step);
  }

  return { neonSequenceIds, neonMovementProfiles, particlePresets, titleAnimationPresets };
}

export { PRESENTATION_CHANNELS };
export type { PresentationChannel };
